import { cn } from "@win-reality/react-ui";
import { ChevronRight } from "lucide-react";

import { OUTS_PER_INNING } from "../../constants/game";
import { ResultingState } from "../../types/gameFull";
import { toBaserunnerFlags } from "../../utils/pitchTagging";
import { formatPlayerName } from "../../utils/playerName";
import { getRoleColors } from "../../utils/teamColors";
import { BaseRunners } from "../BaseRunners";
import { SELECTED_ARROW_CLIP } from "./PitchRow";

type PlateAppearanceHeaderProps = {
  pitcherName: string;
  hitterName: string;
  /** Player external ids, for the pitcher and hitter test values */
  pitcherId?: string;
  hitterId?: string;
  /** Decides which team is hitting, for the role colors */
  isTopOfInning: boolean;
  resultingState?: ResultingState | null;
  awayTeamColor: string;
  homeTeamColor: string;
  isSelected?: boolean;
  /** Omit for a non-interactive header */
  onClick?: () => void;
  /** Test id and instance attribute for the row button, from the call site */
  "data-testid"?: string;
  "data-plate-appearance-id"?: string;
};

export const PlateAppearanceHeader = ({
  pitcherName,
  hitterName,
  pitcherId,
  hitterId,
  isTopOfInning,
  resultingState,
  awayTeamColor,
  homeTeamColor,
  isSelected = false,
  onClick,
  "data-testid": testId,
  "data-plate-appearance-id": plateAppearanceId,
}: PlateAppearanceHeaderProps) => {
  const { hitterColor, pitcherColor } = getRoleColors(
    isTopOfInning,
    awayTeamColor,
    homeTeamColor,
  );

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      data-testid={testId}
      data-plate-appearance-id={plateAppearanceId}
      aria-current={isSelected ? "true" : undefined}
      className={cn(
        // Size
        "w-full",
        // Layout
        "flex items-center justify-between gap-2",
        // Spacing
        "px-3 py-3",
        // Background
        isSelected && "bg-tagging-card",
        // Text
        "text-left",
        // States
        "focus:outline-none disabled:cursor-default",
      )}
      style={isSelected ? { clipPath: SELECTED_ARROW_CLIP } : undefined}
    >
      <div className="min-w-0 text-sm font-bold uppercase leading-tight">
        <div
          data-testid="plate-appearance-pitcher-value"
          data-player-id={pitcherId}
          data-value={pitcherName}
          className="truncate text-gray-900 dark:text-white"
        >
          <span style={{ color: pitcherColor }}>Pitcher</span> -{" "}
          {formatPlayerName(pitcherName)}
        </div>
        <div
          data-testid="plate-appearance-hitter-value"
          data-player-id={hitterId}
          data-value={hitterName}
          className="truncate text-gray-900 dark:text-white"
        >
          <span style={{ color: hitterColor }}>Hitter</span> -{" "}
          {formatPlayerName(hitterName)}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {resultingState && (
          <div className="flex items-center gap-1">
            <span
              data-testid="plate-appearance-score-value"
              data-team="away"
              data-value={resultingState.score.away}
              className="text-base font-bold"
              style={{ color: awayTeamColor }}
            >
              {resultingState.score.away}
            </span>

            <div className="flex flex-col items-center gap-0.5">
              <BaseRunners
                data-testid="plate-appearance-base-runners-panel"
                size="small"
                baserunners={toBaserunnerFlags(resultingState.baseRunners)}
              />
              <div
                data-testid="plate-appearance-outs-value"
                data-value={resultingState.outs}
                className="flex gap-1"
              >
                {Array.from({ length: OUTS_PER_INNING }).map((_, i) => (
                  <div
                    key={i}
                    className={cn(
                      "h-2 w-2 rounded-full",
                      i < resultingState.outs
                        ? "bg-black dark:bg-white"
                        : "border-[1.5px] border-[var(--tagging-count-indicator-border)] bg-transparent",
                    )}
                  />
                ))}
              </div>
            </div>

            <span
              data-testid="plate-appearance-score-value"
              data-team="home"
              data-value={resultingState.score.home}
              className="text-base font-bold"
              style={{ color: homeTeamColor }}
            >
              {resultingState.score.home}
            </span>
          </div>
        )}

        {onClick && (
          <ChevronRight className="h-5 w-5 text-gray-900 dark:text-white" />
        )}
      </div>
    </button>
  );
};
